/*
  Page depth: tilt cards (mouse only), scroll parallax layers (GSAP scrub) and scroll-drawn bars.
  Everything is skipped under reduced motion.
*/
import { perScroller, ready } from '@up/scroll';

const still = matchMedia('(prefers-reduced-motion: reduce)').matches;

/** @param {HTMLElement} el */
function tilt(el) {
  const max = Number(el.dataset.upTilt || 7);
  let frame = 0;
  /** @param {number} x @param {number} y */
  const set = (x, y) => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      el.style.setProperty('--ry', `${(x - 0.5) * max * 2}deg`);
      el.style.setProperty('--rx', `${(0.5 - y) * max * 2}deg`);
      el.style.setProperty('--mx', `${x * 100}%`);
      el.style.setProperty('--my', `${y * 100}%`);
    });
  };
  el.addEventListener('pointermove', (event) => {
    if (event.pointerType !== 'mouse') return;
    const r = el.getBoundingClientRect();
    el.classList.add('is-tilting');
    set((event.clientX - r.left) / r.width, (event.clientY - r.top) / r.height);
  });
  el.addEventListener('pointerleave', () => {
    el.classList.remove('is-tilting');
    set(0.5, 0.5);
  });
}

/** @param {ParentNode} scope */
async function init(scope) {
  if (still) return;
  const cards = /** @type {HTMLElement[]} */ ([...scope.querySelectorAll('[data-up-tilt]')]);
  cards.forEach(tilt);
  // Device tilt on phones is off: flat cards scroll far more smoothly (see up-system.css).

  const layers = /** @type {HTMLElement[]} */ ([...scope.querySelectorAll('[data-up-parallax]')]);
  const draws = /** @type {HTMLElement[]} */ ([...scope.querySelectorAll('[data-up-draw]')]);
  if (!layers.length && !draws.length) return;
  const lib = await ready();
  if (!lib) return;
  const { gsap } = lib;
  for (const el of layers) {
    const host = el.closest('[data-up-parallax-host]') || el.parentElement || el;
    const amount = Number(el.dataset.upParallax || 0.2);
    perScroller(host, (scroller) => {
      const tween = gsap.fromTo(
        el,
        { yPercent: -amount * 50 },
        { yPercent: amount * 50, ease: 'none', scrollTrigger: { trigger: host, scroller, start: 'top bottom', end: 'bottom top', scrub: 0.5 } }
      );
      return () => {
        tween.scrollTrigger?.kill();
        tween.kill();
      };
    });
  }
  for (const el of draws) {
    const host = el.closest('[data-up-draw-host]') || el;
    perScroller(host, (scroller) => {
      const tween = gsap.fromTo(
        el,
        { '--draw': 0 },
        { '--draw': 1, ease: 'none', scrollTrigger: { trigger: host, scroller, start: el.dataset.upDrawStart || 'top 75%', end: el.dataset.upDrawEnd || 'bottom 60%', scrub: 0.5 } }
      );
      return () => {
        tween.scrollTrigger?.kill();
        tween.kill();
      };
    });
  }
}

init(document);
document.addEventListener('shopify:section:load', (event) => event.target instanceof Element && init(event.target));
